import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero, openUrl } from "@/utils/utils";
import { Card, Divider, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFilterNotifications, useToggleNotificationSentAndSeenStatus } from "../../../../common/api/queries";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { NotificationStatus, NotificationTopic } from "../../enums";
import { Notification, NotificationFilterPageRequest, SysadminBugReportAdditionalData } from "../../types";
import { getNotificationTopicPage } from "../../utils";
import { BugReportNotificationView } from "./BugReportNotificationView";

interface Props {
  searchParams: NotificationFilterPageRequest;
}

const NotificationTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedBugReportNotification, setSelectedBugReportNotification] = useState<Notification>();

  const { data, isFetching } = useFilterNotifications(searchParams);
  const toggleNotificationSentAndSeenStatus = useToggleNotificationSentAndSeenStatus();

  const handleNotificationShowClick = (notification: Notification) => {
    if (notification.status === NotificationStatus.SENT) {
      toggleNotificationSentAndSeenStatus.mutate({ id: notification.id });
    }

    if (notification.data.topic === NotificationTopic.SYSADMIN_BUG_REPORT_CREATED) {
      setSelectedBugReportNotification(notification);
    } else {
      const topicPageUrl = getNotificationTopicPage(notification);

      if (topicPageUrl) {
        openUrl(topicPageUrl, "_blank");
      }
    }
  };

  const columns: ColumnsType<Notification> = [
    {
      key: "title",
      title: t("notification.filter.title"),
      width: 170,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.data.title}</Ellipsis>,
    },
    {
      key: "content",
      title: t("notification.filter.content"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.data.content}</Ellipsis>,
    },
    {
      key: "sentAt",
      title: t("notification.filter.sentAt"),
      width: 160,
      render: (_, record) => (
        <Tooltip title={record.sentAt ? formatLocaleDateTime(record.sentAt) : undefined}>
          {record.sentAt ? dayjs(record.sentAt).fromNow() : undefined}
        </Tooltip>
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 300,
      render: (_, record) => (
        <div className="nowrap">
          {record.status === NotificationStatus.SEEN ? (
            <ActionTextIcon
              icon="eye-invisible"
              text={t("notification.actions.markAsUnseen")}
              color="blue"
              onClick={() => toggleNotificationSentAndSeenStatus.mutate({ id: record.id })}
            />
          ) : (
            <ActionTextIcon
              icon="eye"
              text={t("notification.actions.markAsSeen")}
              color="red"
              onClick={() => toggleNotificationSentAndSeenStatus.mutate({ id: record.id })}
            />
          )}

          <Divider orientation="vertical" />

          <ActionTextIcon
            icon="arrows"
            color="green"
            text={t("common.show")}
            onClick={() => handleNotificationShowClick(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<Notification>
        {...tableStandardProps(true, false)}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        loading={isFetching}
        rowClassName={(record, index) =>
          record.status === NotificationStatus.SENT
            ? "table-row-unread"
            : index % 2 === 0
              ? "table-row-light"
              : "table-row-dark"
        }
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      {selectedBugReportNotification ? (
        <BugReportNotificationView
          open={!!selectedBugReportNotification}
          id={(selectedBugReportNotification.data.additionalData as SysadminBugReportAdditionalData).bugReportId}
          onCancelClick={() => setSelectedBugReportNotification(undefined)}
        />
      ) : undefined}
    </Card>
  );
};

export default NotificationTable;
